import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, User, ArrowRight } from 'lucide-react';
import { setStoredToken } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Register() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Registration failed');
      }

      const data = await response.json();
      // Store token in localStorage for Bearer-header auth (robust across CDN cookie quirks)
      if (data.token) setStoredToken(data.token);
      navigate('/dashboard', { state: { user: data.user } });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="grid-background absolute inset-0 opacity-20"></div>
      
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="text-6xl glow mb-4">⚡</div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-[#00f0ff] via-[#ff00ff] to-[#b026ff] text-transparent bg-clip-text mb-2">
            Join Next Wave
          </h1>
          <p className="text-[#00f0ff]/70">Create your account and get started</p>
        </div>

        <div className="glass rounded-2xl p-8 glow-box">
          {error && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400" data-testid="error-message">
              {error}
            </div>
          )}

          <form onSubmit={handleRegister} className="space-y-6">
            <div>
              <label className="block text-[#00f0ff] mb-2 font-semibold">Full Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#00f0ff]/50" size={20} />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-[#0a0a1f]/50 border border-[#00f0ff]/30 rounded-lg text-[#00f0ff] focus:border-[#ff00ff] focus:outline-none transition-all"
                  placeholder="John Doe"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-[#00f0ff] mb-2 font-semibold">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#00f0ff]/50" size={20} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-[#0a0a1f]/50 border border-[#00f0ff]/30 rounded-lg text-[#00f0ff] focus:border-[#ff00ff] focus:outline-none transition-all"
                  placeholder="your@email.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-[#00f0ff] mb-2 font-semibold">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#00f0ff]/50" size={20} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-[#0a0a1f]/50 border border-[#00f0ff]/30 rounded-lg text-[#00f0ff] focus:border-[#ff00ff] focus:outline-none transition-all"
                  placeholder="••••••••"
                  required
                  minLength="6"
                  data-testid="password-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 btn-primary text-white font-bold rounded-lg flex items-center justify-center space-x-2 disabled:opacity-50"
              data-testid="register-button"
            >
              {loading ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <span>Create Account</span>
                  <ArrowRight size={20} />
                </>
              )}
            </button>
          </form>

          <div className="my-6 flex items-center">
            <div className="flex-1 border-t border-[#00f0ff]/30"></div>
            <span className="px-4 text-[#00f0ff]/50">OR</span>
            <div className="flex-1 border-t border-[#00f0ff]/30"></div>
          </div>

          <button
            onClick={handleGoogleSignup}
            className="w-full py-3 border-2 border-[#00f0ff] text-[#00f0ff] font-bold rounded-lg hover:bg-[#00f0ff] hover:text-[#0a0a1f] transition-all flex items-center justify-center space-x-2"
            data-testid="google-signup-button"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span>Sign up with Google</span>
          </button>

          <p className="mt-6 text-center text-[#00f0ff]/70">
            Already have an account?{' '}
            <Link to="/login" className="text-[#ff00ff] font-semibold hover:underline" data-testid="login-link">
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register;